import { useState, useEffect, useRef, useMemo, useCallback, type KeyboardEvent } from 'react'
import {
  useProductTemplates,
  useProductTemplate,
  useTemplateAttributes,
  useCreateProductTemplate,
  useUpdateProductTemplate,
  useDeleteProductTemplate,
  useCreateTemplateAttribute,
  useUpdateTemplateAttribute,
  useDeleteTemplateAttribute,
  useReorderTemplateAttributes,
} from '@/admin/hooks/use-products'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select'
import { AttributeValidationEditor } from '@/admin/components/shared/attribute-validation-editor'
import { canUseLoadedValues, readQrConfig, readStockConfig } from '@/admin/lib/value-stock'
import {
  draftsFromRules,
  rulesFromDrafts,
} from '@/admin/components/shared/attribute-validation-rules'
import type { ValidationRule } from '@/shared/lib/forms'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { Pencil, Trash2, X, GripVertical } from 'lucide-react'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import { AttributeDeleteDialog } from '@/admin/components/shared/attribute-delete-dialog'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import {
  EntityAttributeDialog,
  EntityDisplayTab,
  EntityRegistrationSection,
  EntityActionsTab,
  type DisplayColumnPriority,
} from '@/admin/pages/manage/entities/entity-editor-page'
import { useEntityTypes, type EntityTypeDefinition } from '@/admin/hooks/use-entities'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { useHashTab, MANAGE_TEMPLATE_TABS } from '@/admin/hooks/use-hash-tab'
import { useHostTemplates } from '@/admin/hooks/use-host-templates'
import { useTransactionTemplates } from '@/admin/hooks/use-transaction-templates'
import type { AttributeLevel, FieldType, ProductAttribute } from '@/admin/types/products'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

import { toastApiError } from '@/shared/lib/api-error'
const FIELD_TYPES: FieldType[] = [
  'TEXT', 'RICH_TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT',
  'DATE', 'DATE_TIME', 'DOCUMENT', 'FILE', 'IMAGE', 'VIDEO',
  'LINK', 'MONEY', 'ADDRESS', 'PHONE', 'EMAIL',
]

// ─── HoldingEntityGate ────────────────────────────────────
//
// Renders the Registration / Display tab content for the holding
// entity, surfacing the three real failure states cleanly:
//
//   1. Loading  - entity catalog still being fetched.
//   2. Error    - fetch failed, offer a retry.
//   3. Missing  - fetch succeeded, but no ``holding`` row came back.
//      With the default-org-bootstrap invariant enforced server-side
//      (``apps.organisations.services.org_bootstrap``), this state
//      should be unreachable for healthy tenants - treat it as a
//      platform error rather than a "not seeded yet" state.

function HoldingEntityGate({
  isLoading,
  isError,
  onRetry,
  holdingEntity,
  children,
}: {
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  holdingEntity: EntityTypeDefinition | undefined
  children: (entity: EntityTypeDefinition) => React.ReactNode
}) {
  // Keys are looked up by the API-backed translations backend
  // (``frontend/src/admin/lib/i18n.ts``); ``defaultValue`` keeps the
  // English copy visible until the keys are added to the tenant
  // translations catalog server-side.
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-24 animate-pulse items-center justify-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[12px] text-[var(--ink-3)]"
      >
        {t('manage.productTemplates.holdingEntityGate.loading', {
          defaultValue: 'Loading entity catalogue…',
        })}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-2 rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-2)]">
        <span>
          {t('manage.productTemplates.holdingEntityGate.error', {
            defaultValue: 'Couldn’t load the entity catalogue.',
          })}
        </span>
        <Button variant="outline" size="sm" onClick={onRetry} className="self-start">
          {t('common.retry', { defaultValue: 'Retry' })}
        </Button>
      </div>
    )
  }

  if (!holdingEntity) {
    return (
      <div className="rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-2)]">
        <p className="font-medium">
          {t('manage.productTemplates.holdingEntityGate.bootstrapErrorTitle', {
            defaultValue: 'Platform bootstrap error',
          })}
        </p>
        <p className="mt-1 text-[var(--ink-3)]">
          {t(
            'manage.productTemplates.holdingEntityGate.bootstrapErrorDescription',
            {
              defaultValue:
                'The holding entity is missing for this tenant. New tenants materialise this entity automatically; if you’re seeing this message, the default-org bootstrap did not run for the tenant. Contact support.',
            },
          )}
        </p>
      </div>
    )
  }

  return <>{children(holdingEntity)}</>
}

function useLevelLabels(): Record<AttributeLevel, string> {
  const { t } = useTranslation()
  return {
    PRODUCT: t('templates.levelProduct'),
    PRODUCT_VARIANT: t('templates.levelVariant'),
    HOLDING: 'Registration field',
  }
}

function useFieldTypeLabels(): Record<FieldType, string> {
  const { t } = useTranslation()
  return {
    TEXT: t('templates.fieldType.text'),
    RICH_TEXT: t('templates.fieldType.richText'),
    NUMBER: t('templates.fieldType.number'),
    BOOLEAN: t('templates.fieldType.boolean'),
    SELECT: t('templates.fieldType.select'),
    MULTI_SELECT: t('templates.fieldType.multiSelect'),
    DATE: t('templates.fieldType.date'),
    DATE_TIME: t('templates.fieldType.dateTime'),
    DOCUMENT: t('templates.fieldType.document'),
    FILE: t('templates.fieldType.file'),
    IMAGE: t('templates.fieldType.image'),
    VIDEO: t('templates.fieldType.video'),
    LINK: t('templates.fieldType.link'),
    MONEY: t('templates.fieldType.money'),
    ADDRESS: t('templates.fieldType.address'),
    PHONE: t('templates.fieldType.phone'),
    EMAIL: t('templates.fieldType.email'),
  }
}

function isSelectType(ft: FieldType) {
  return ft === 'SELECT' || ft === 'MULTI_SELECT'
}

/** Options come from the backend as either plain strings or {label, value} objects. Normalise to strings for display. */
function normaliseOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((o) => {
    if (typeof o === 'string') return o
    if (o && typeof o === 'object' && 'label' in o) return String(o.label)
    if (o && typeof o === 'object' && 'value' in o) return String(o.value)
    return String(o)
  })
}

/** Convert string array back to {label, value} objects for the API. */
function optionsToPayload(opts: string[]): Array<{ label: string; value: string }> {
  return opts.map((o) => ({ label: o, value: o }))
}

export function ProductTemplatesPage() {
  const { t } = useTranslation()
  const { data: templates, isLoading } = useProductTemplates()
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const createTemplate = useCreateProductTemplate()
  const [detailDirty, setDetailDirty] = useState(false)
  const [pendingSelection, setPendingSelection] = useState<{ uuid: string | null } | null>(null)

  // Guard template switches: if the open template has unsaved header edits,
  // confirm before discarding them instead of silently dropping the changes.
  function requestSelect(uuid: string | null) {
    if (uuid === selectedUuid) return
    if (detailDirty) {
      setPendingSelection({ uuid })
      return
    }
    setSelectedUuid(uuid)
  }

  function confirmDiscardSelection() {
    if (pendingSelection) {
      setDetailDirty(false)
      setSelectedUuid(pendingSelection.uuid)
    }
    setPendingSelection(null)
  }

  async function handleCreateTemplate() {
    if (!newTemplateName.trim()) return
    try {
      const created = await createTemplate.mutateAsync({ name: newTemplateName.trim() })
      setSelectedUuid(created.uuid)
      setNewTemplateName('')
      setShowCreateDialog(false)
      toast.success(t('templates.toasts.created'))
    } catch (err) {
      toastApiError(err, t('templates.toasts.createFailed'));
    }
  }

  return (
    <>
      <MasterDetail
        testId="product-templates"
        title={t('templates.title')}
        onAdd={() => setShowCreateDialog(true)}
        hasSelection={!!selectedUuid}
        selectedId={selectedUuid}
        visibleItemIds={(templates ?? []).map((template) => template.uuid)}
        onSelectionChange={requestSelect}
        onBack={() => requestSelect(null)}
        list={
          <>
            {isLoading ? (
              <p className="text-[12px] text-[var(--ink-3)]">{t('common.loading')}</p>
            ) : !templates?.length ? (
              <p className="text-[12px] text-[var(--ink-3)]">{t('templates.emptyState')}</p>
            ) : (
              <div className="flex flex-col gap-0.5">
                {templates.map((tmpl) => {
                  const isActive = tmpl.uuid === selectedUuid
                  return (
                    <Button
                      variant="ghost"
                      key={tmpl.uuid}
                      data-testid={`product-templates-item-${tmpl.uuid}`}
                      onClick={() => requestSelect(tmpl.uuid)}
                      className={cn(
                        'h-auto w-full min-w-0 shrink cursor-pointer justify-start overflow-hidden whitespace-normal rounded-[4px] px-3 py-2 text-left text-[12px] leading-snug break-words transition-all duration-150',
                        isActive
                          ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                          : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]',
                      )}
                    >
                      {tmpl.name}
                    </Button>
                  );
                })}
              </div>
            )}
          </>
        }
        detail={
          selectedUuid ? (
            <TemplateDetail
              key={selectedUuid}
              templateUuid={selectedUuid}
              onDeleted={() => setSelectedUuid(null)}
              onDirtyChange={setDetailDirty}
            />
          ) : (
            <div className="flex justify-center pt-24">
              <p className="text-[13px] text-[var(--ink-3)]">
                {t('templates.selectTemplate')}
              </p>
            </div>
          )
        }
      />
      {/* Create template dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('templates.newTemplate')}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="clara-section-label mb-2 block">{t('common.name')}</Label>
            <Input
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder={t('templates.namePlaceholder')}
              className="text-[13px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateTemplate()
              }}
            />
          </div>
          <DialogFooter>
            <GreenButton
              data-testid="product-templates-dialog-submit"
              onClick={handleCreateTemplate}
              disabled={!newTemplateName.trim() || createTemplate.isPending}
            >
              {t('common.create')}
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Unsaved-changes guard when switching templates */}
      <Dialog
        open={!!pendingSelection}
        onOpenChange={(open) => {
          if (!open) setPendingSelection(null)
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('templates.unsavedTitle', 'Unsaved changes')}</DialogTitle>
            <DialogDescription>
              {t(
                'templates.unsavedBody',
                'You have unsaved changes to this template. Switch anyway and discard them?',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingSelection(null)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDiscardSelection}>
              {t('templates.discardChanges', 'Discard changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Template Detail ─────────────────────────────────────

function TemplateDetail({
  templateUuid,
  onDeleted,
  onDirtyChange,
}: {
  templateUuid: string
  onDeleted: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useHashTab(MANAGE_TEMPLATE_TABS, "general")
  const { data: template, isLoading: templateLoading } = useProductTemplate(templateUuid)
  const { data: attributes, isLoading: attrsLoading } = useTemplateAttributes(templateUuid)
  const {
    data: entityTypes = [],
    isLoading: entityTypesLoading,
    isError: entityTypesError,
    refetch: refetchEntityTypes,
  } = useEntityTypes()
  const updateTemplate = useUpdateProductTemplate()
  const deleteTemplate = useDeleteProductTemplate()
  const [showAttrDialog, setShowAttrDialog] = useState(false)
  const [showComputedFieldDialog, setShowComputedFieldDialog] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingAttr, setEditingAttr] = useState<ProductAttribute | null>(null)
  // Prefills the create modal's display name + level when the user chooses
  // "create new" from a section's add-attribute picker.
  const [prefillName, setPrefillName] = useState('')
  const [prefillLevel, setPrefillLevel] = useState<AttributeLevel>('PRODUCT')
  const addAttr = useCreateTemplateAttribute()
  const resourceLookupProviders = useResourceLookupProviders()

  const [editName, setEditName] = useState('')
  const [editShortFormat, setEditShortFormat] = useState('')
  const [editLongFormat, setEditLongFormat] = useState('')
  const [editVarShortFormat, setEditVarShortFormat] = useState('')
  const [editVarLongFormat, setEditVarLongFormat] = useState('')
  const [editHostTemplateUuid, setEditHostTemplateUuid] = useState<string | null>(null)
  const [editHostRequirement, setEditHostRequirement] = useState<string>('NONE')
  const [editTransactionTemplateUuid, setEditTransactionTemplateUuid] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  const { data: hostTemplates } = useHostTemplates()
  const { data: transactionTemplates } = useTransactionTemplates()

  // Sync local state when template loads
  const prevUuidRef = useRef<string | null>(null)
  useEffect(() => {
    if (template && prevUuidRef.current !== templateUuid) {
      prevUuidRef.current = templateUuid
       
      setEditName(template.name)
      setEditShortFormat(template.short_name_format)
      setEditLongFormat(template.long_name_format)
      setEditVarShortFormat(template.variant_short_name_format)
      setEditVarLongFormat(template.variant_long_name_format)
      setEditHostTemplateUuid(template.host_template?.uuid ?? null)
      setEditHostRequirement(template.host_requirement ?? 'NONE')
      setEditTransactionTemplateUuid(template.transaction_template?.uuid ?? null)
      setIsDirty(false)

    }
  }, [template, templateUuid])

  // The host/transaction selects auto-save, so the loaded template is always
  // their source of truth - there is no unsaved edit to protect (unlike the
  // free-text name/format inputs guarded by the once-per-uuid sync above).
  // Keep them synced whenever the template's own values change, so a fresh
  // background refetch that replaces a stale persisted-cache snapshot (e.g.
  // after a full page reload) still updates the select. Without this, the
  // guarded sync above runs once against the stale (null) snapshot and the
  // later real value never reaches the trigger - the field stays on "None".
  useEffect(() => {
    if (!template) return
    setEditHostTemplateUuid(template.host_template?.uuid ?? null)
    setEditHostRequirement(template.host_requirement ?? 'NONE')
    setEditTransactionTemplateUuid(template.transaction_template?.uuid ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    template?.host_template?.uuid,
    template?.host_requirement,
    template?.transaction_template?.uuid,
  ])

  // Bubble unsaved header edits up so the page can guard template switches.
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Report clean on unmount so a stale dirty flag can't block the next switch.
  // Separate effect (deps: onDirtyChange only) so the sync above doesn't fire a
  // redundant false->true on every isDirty change.
  useEffect(() => {
    return () => onDirtyChange?.(false)
  }, [onDirtyChange])

  // Warn before a browser refresh/close silently discards header edits.
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  async function handleSave() {
    if (!template) return
    try {
      await updateTemplate.mutateAsync({
        uuid: template.uuid,
        name: editName,
        short_name_format: editShortFormat,
        long_name_format: editLongFormat,
        variant_short_name_format: editVarShortFormat,
        variant_long_name_format: editVarLongFormat,
        host_template_uuid: editHostTemplateUuid,
        host_requirement: editHostTemplateUuid ? editHostRequirement : 'NONE',
        transaction_template_uuid: editTransactionTemplateUuid,
      })
      setIsDirty(false)
      toast.success(t('templates.toasts.saved'))
    } catch (err) {
      toastApiError(err, t('templates.toasts.saveFailed'));
    }
  }

  async function handleDeleteTemplate() {
    if (!template) return
    const force = (template.product_count ?? 0) > 0
    try {
      await deleteTemplate.mutateAsync({ uuid: template.uuid, force })
      toast.success('Template deleted')
      setDeleteDialogOpen(false)
      onDeleted()
    } catch (err) {
      toastApiError(err, 'Failed to delete template');
    }
  }

  function markDirty() { setIsDirty(true) }

  // Auto-save for the host/transaction template selects. Unlike the free-text
  // name/format inputs (which commit via the explicit Save button), a dropdown
  // selection reads as instantly applied - deferring it to a Save button the
  // user may not notice silently drops the change and leaves the template out
  // of sync with what they picked. These fields persist the moment they change.
  // The PUT is partial (see ProductTemplateDetailView.put), so sending only the
  // changed fields never clobbers a pending, unsaved name/format edit.
  // The select handlers apply the change optimistically to local state before
  // the PUT resolves. If the PUT fails, `rollback` restores the previous local
  // value so the UI never shows a selection the server did not accept - the
  // exact desync this auto-save is meant to eliminate.
  async function persistFields(
    fields: {
      host_template_uuid?: string | null
      host_requirement?: string
      transaction_template_uuid?: string | null
    },
    rollback?: () => void,
  ) {
    if (!template) return
    try {
      await updateTemplate.mutateAsync({ uuid: template.uuid, ...fields })
      toast.success(t('templates.toasts.saved'))
    } catch (err) {
      rollback?.()
      toastApiError(err, t('templates.toasts.saveFailed'))
    }
  }

  function openEditAttr(attr: ProductAttribute) {
    setEditingAttr(attr)
    setShowAttrDialog(true)
  }

  // Add-attribute entry point, scoped to a section's level (Product / Variant /
  // Registration field). Selecting an existing shared attribute attaches it to
  // this template immediately at that level (the backend reuses the bucket
  // definition, so field_type + options stay consistent). Choosing "create
  // new" opens the full attribute modal, prefilled with the typed name + level.
  //
  // Registration fields (HOLDING) persist any unsaved template header edits
  // first, so the field attaches to the up-to-date template, not a stale one.
  async function handleAddAttrPick(
    level: AttributeLevel,
    result: AttributeTemplatePickResult,
  ) {
    if (level === 'HOLDING' && isDirty) {
      await handleSave()
    }
    if (result.type === 'create') {
      setPrefillName(result.name ?? '')
      setPrefillLevel(level)
      setEditingAttr(null)
      setShowAttrDialog(true)
      return
    }
    if (result.type === 'existing') {
      const tpl: AttributeTemplateResult = result.template
      try {
        await addAttr.mutateAsync({
          templateUuid,
          display_name: tpl.display_name,
          code_name: tpl.code_name,
          description: tpl.description ?? '',
          field_type: tpl.field_type,
          attribute_level: level,
          required_to_save: false,
          required_to_publish: true,
          visibility: 'PUBLIC',
          configuration_data: tpl.configuration_data ?? {},
        })
        toast.success(t('templates.toasts.attributeAdded'))
      } catch (err) {
        toastApiError(err, t('templates.toasts.attributeAddFailed'))
      }
    }
  }

  if (templateLoading) {
    return <p className="text-[13px] text-[var(--ink-3)]">{t('common.loading')}</p>;
  }

  if (!template) return null

  // Group attributes by level
  const grouped: Record<AttributeLevel, ProductAttribute[]> = {
    PRODUCT: [],
    PRODUCT_VARIANT: [],
    HOLDING: [],
  }
  attributes?.forEach((a) => {
    if (grouped[a.attribute_level]) {
      grouped[a.attribute_level].push(a)
    }
  })

  // Registration fields whose values are pre-loaded. Named in the VARIANT
  // section so an admin looking there for "where do the ranges live" is told,
  // rather than having to open every registration field to find out.
  const stockedNames = grouped.HOLDING.filter((a) =>
    readStockConfig(a.configuration_data),
  ).map((a) => a.display_name)

  const productCount = template.product_count ?? 0
  const forceDelete = productCount > 0
  const holdingEntity = entityTypes.find((entity) => entity.key === 'holding')
  const priorityFields: DisplayColumnPriority[] = (attributes ?? []).map((attribute) => ({
    codeName: attribute.code_name,
    entityKeys: attribute.attribute_level === 'HOLDING' ? ['holding'] : ['product'],
    sourceModel: 'products.ProductAttribute',
    sourceOwnerUuid: template.uuid,
    sourceAttributeUuid: attribute.uuid,
  }))

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <div>
      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsList variant="line">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="registration">Registration</TabsTrigger>
          <TabsTrigger value="display">Display</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          {/* Template name + save */}
          <div className="mb-6 flex items-center justify-between">
            <Input
              value={editName}
              onChange={(e) => { setEditName(e.target.value); markDirty() }}
              className="max-w-md border-none px-0 font-display text-[20px] text-[var(--ink)]"
            />
            {isDirty && (
              <GreenButton
                onClick={handleSave}
                disabled={updateTemplate.isPending}
                data-testid="product-templates-save-btn"
              >
                {t('common.save')}
              </GreenButton>
            )}
          </div>
          {/* Name format fields */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div>
              <Label className="clara-section-label mb-1.5 block">{t('templates.shortNameFormat', 'Short name format')}</Label>
              <Input
                value={editShortFormat}
                onChange={(e) => { setEditShortFormat(e.target.value); markDirty() }}
                className="text-[13px]"
                placeholder="{brand} {model}"
              />
              <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{t('templates.shortNameFormatHint', "Template that builds a product's display name from its fields. Reference product fields by code name, e.g. {brand} {model}. You don't set the name directly - this is the pattern the name is generated from.")}</p>
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">{t('templates.longNameFormat', 'Long name format')}</Label>
              <Input
                value={editLongFormat}
                onChange={(e) => { setEditLongFormat(e.target.value); markDirty() }}
                className="text-[13px]"
                placeholder="{brand} {model} {year}"
              />
              <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{t('templates.longNameFormatHint', 'Longer display name pattern, built the same way from product fields, e.g. {brand} {model} {year}.')}</p>
            </div>
          </div>
          {/* Variant name formats */}
          {template.has_variant_attributes && (
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div>
                <Label className="clara-section-label mb-1.5 block">{t('templates.variantShortNameFormat', 'Variant short name format')}</Label>
                <Input
                  value={editVarShortFormat}
                  onChange={(e) => { setEditVarShortFormat(e.target.value); markDirty() }}
                  className="text-[13px]"
                  placeholder="{colour} {size}"
                />
                <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{t('templates.variantShortNameFormatHint', 'Display name pattern for each variant. Can use product and variant fields, e.g. {colour} {size}.')}</p>
              </div>
              <div>
                <Label className="clara-section-label mb-1.5 block">{t('templates.variantLongNameFormat', 'Variant long name format')}</Label>
                <Input
                  value={editVarLongFormat}
                  onChange={(e) => { setEditVarLongFormat(e.target.value); markDirty() }}
                  className="text-[13px]"
                  placeholder="{colour} {size} {sku}"
                />
                <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{t('templates.variantLongNameFormatHint', 'Longer variant display name pattern, built from product and variant fields, e.g. {colour} {size} {sku}.')}</p>
              </div>
            </div>
          )}
          {/* Host template */}
          <div className="mb-6">
            <Label className="clara-section-label mb-1.5 block">Host Template</Label>
            <p className="mb-2 text-[11px] text-[var(--ink-3)]">
              Select which host type products in this template attach to, or leave as "None" for standalone products.
            </p>
            <div className="flex items-start gap-3">
              <Select
                value={editHostTemplateUuid ?? '__none__'}
                onValueChange={(v) => {
                  const prevUuid = editHostTemplateUuid
                  const prevRequirement = editHostRequirement
                  const nextUuid = v === '__none__' ? null : v
                  let nextRequirement = editHostRequirement
                  if (nextUuid === null) nextRequirement = 'NONE'
                  else if (editHostRequirement === 'NONE') nextRequirement = 'REQUIRED'
                  setEditHostTemplateUuid(nextUuid)
                  setEditHostRequirement(nextRequirement)
                  void persistFields(
                    {
                      host_template_uuid: nextUuid,
                      host_requirement: nextUuid ? nextRequirement : 'NONE',
                    },
                    () => {
                      setEditHostTemplateUuid(prevUuid)
                      setEditHostRequirement(prevRequirement)
                    },
                  )
                }}
              >
                <SelectTrigger className="w-[240px] text-[13px]" data-testid="product-templates-host-select">
                  <SelectValue placeholder="Select host template…">
                    {editHostTemplateUuid
                      ? hostTemplates?.find((ht) => ht.uuid === editHostTemplateUuid)?.name ?? 'Loading…'
                      : 'None (standalone)'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (standalone)</SelectItem>
                  {hostTemplates?.map((ht) => (
                    <SelectItem key={ht.uuid} value={ht.uuid}>
                      {ht.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {editHostTemplateUuid && (
                <Select
                  value={editHostRequirement}
                  onValueChange={(v) => {
                    const prevRequirement = editHostRequirement
                    setEditHostRequirement(v)
                    void persistFields(
                      {
                        host_template_uuid: editHostTemplateUuid,
                        host_requirement: v,
                      },
                      () => setEditHostRequirement(prevRequirement),
                    )
                  }}
                >
                  <SelectTrigger className="w-[160px] text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REQUIRED">Required</SelectItem>
                    <SelectItem value="OPTIONAL">Optional</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          {/* Transaction template */}
          <div className="mb-6">
            <Label className="clara-section-label mb-1.5 block">Transaction Template</Label>
            <p className="mb-2 text-[11px] text-[var(--ink-3)]">
              Select which transaction template defines the purchase fields for registrations of this product, or leave as &ldquo;None&rdquo; for default fields.
            </p>
            <Select
              value={editTransactionTemplateUuid ?? '__none__'}
              onValueChange={(v) => {
                const prevUuid = editTransactionTemplateUuid
                const nextUuid = v === '__none__' ? null : v
                setEditTransactionTemplateUuid(nextUuid)
                void persistFields(
                  { transaction_template_uuid: nextUuid },
                  () => setEditTransactionTemplateUuid(prevUuid),
                )
              }}
            >
              <SelectTrigger className="w-[240px] text-[13px]" data-testid="product-templates-transaction-select">
                <SelectValue placeholder="Select transaction template…">
                  {editTransactionTemplateUuid
                    ? transactionTemplates?.find((tt) => tt.uuid === editTransactionTemplateUuid)?.name ?? 'Loading…'
                    : 'None (default fields)'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (default fields)</SelectItem>
                {transactionTemplates?.map((tt) => (
                  <SelectItem key={tt.uuid} value={tt.uuid}>
                    {tt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Attributes - each section (Product / Variant / Registration
              field) has its own "+ Add attribute" select-or-create picker,
              scoped to that section's level and the product domain. */}
          <div className="mb-4 border-t-[0.5px] border-t-[var(--rule)] pt-6">
            <div className="mb-4">
              <span className="clara-section-label">{t('templates.attributes')}</span>
            </div>

            {attrsLoading ? (
              <p className="text-[12px] text-[var(--ink-3)]">{t('common.loading')}</p>
            ) : (
              Object.entries(grouped).map(([level, attrs]) => (
                <SortableAttributeSection
                  key={level}
                  level={level as AttributeLevel}
                  attrs={attrs}
                  templateUuid={templateUuid}
                  stockedNames={stockedNames}
                  onEditAttr={openEditAttr}
                  onAddPick={(result) => handleAddAttrPick(level as AttributeLevel, result)}
                />
              ))
            )}
          </div>
          <section className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-6">
            <span className="clara-section-label">Danger</span>
            <div className="mt-4 rounded-[8px] border-[0.5px] border-red-200 bg-red-50/40 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-[13px] font-medium text-red-900">
                    {forceDelete ? 'Delete product template and products' : 'Delete product template'}
                  </h3>
                  <p className="mt-1 text-[12px] leading-[1.5] text-red-800">
                    {forceDelete
                      ? `This template is used by ${productCount} product${productCount === 1 ? '' : 's'}. Deleting it will also delete those products.`
                      : 'This template is not used by any products and can be removed from the active template list.'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={deleteTemplate.isPending}
                  className="shrink-0"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {forceDelete ? 'Delete template and products' : 'Delete template'}
                </Button>
              </div>
            </div>
          </section>
        </TabsContent>
        <TabsContent value="registration">
          <HoldingEntityGate
            isLoading={entityTypesLoading}
            isError={entityTypesError}
            onRetry={refetchEntityTypes}
            holdingEntity={holdingEntity}
          >
            {(entity) => (
              <EntityRegistrationSection
                key={entity.uuid}
                entity={entity}
                allEntities={entityTypes}
              />
            )}
          </HoldingEntityGate>
        </TabsContent>
        <TabsContent value="display">
          <HoldingEntityGate
            isLoading={entityTypesLoading}
            isError={entityTypesError}
            onRetry={refetchEntityTypes}
            holdingEntity={holdingEntity}
          >
            {(entity) => (
              <EntityDisplayTab
                entity={entity}
                allEntities={entityTypes}
                onAddComputedField={() => setShowComputedFieldDialog(true)}
                sections={['registration_table', 'registration_card']}
                priorityFields={priorityFields}
              />
            )}
          </HoldingEntityGate>
        </TabsContent>
        <TabsContent value="actions">
          {holdingEntity ? (
            <EntityActionsTab entityUuid={holdingEntity.uuid} entityKey={holdingEntity.key} />
          ) : (
            <p className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-3)]">
              Actions settings are available once the holding entity has been seeded.
            </p>
          )}
        </TabsContent>
      </Tabs>
      {/* Attribute create/edit dialog */}
      <AttributeDialog
        key={editingAttr?.uuid ?? 'new-attr'}
        templateUuid={templateUuid}
        otherQrField={
          (attributes ?? []).find(
            (attr) =>
              attr.uuid !== editingAttr?.uuid &&
              Boolean(readQrConfig(attr.configuration_data)),
          )?.display_name ?? null
        }
        open={showAttrDialog}
        onOpenChange={(open) => {
          setShowAttrDialog(open)
          if (!open) {
            setEditingAttr(null)
            setPrefillName('')
          }
        }}
        editing={editingAttr}
        prefillName={prefillName}
        prefillLevel={prefillLevel}
      />
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={forceDelete ? 'Delete template and products?' : 'Delete template?'}
        description={
          <>
            Delete <strong>{template.name}</strong>
            {forceDelete ? ` and ${productCount} product${productCount === 1 ? '' : 's'}` : ''}?
            This action cannot be undone.
          </>
        }
        confirmLabel={forceDelete ? 'Delete template and products' : 'Delete template'}
        pendingLabel="Deleting..."
        destructive
        onConfirm={handleDeleteTemplate}
        isPending={deleteTemplate.isPending}
      />
      {holdingEntity ? (
        <EntityAttributeDialog
          entity={holdingEntity}
          allEntities={entityTypes}
          attribute={undefined}
          open={showComputedFieldDialog}
          onOpenChange={setShowComputedFieldDialog}
          defaultOrder={holdingEntity.attributes.length}
          defaults={{ source: 'COMPUTED' }}
        />
      ) : null}
    </div>
    </ResourceLookupContextProvider>
  );
}

// ─── Sortable Attribute Section ──────────────────────────

function SortableAttributeSection({
  level,
  attrs,
  templateUuid,
  stockedNames,
  onEditAttr,
  onAddPick,
}: {
  level: AttributeLevel
  attrs: ProductAttribute[]
  templateUuid: string
  /** Display names of this template's stocked registration fields. */
  stockedNames: string[]
  onEditAttr: (attr: ProductAttribute) => void
  onAddPick: (result: AttributeTemplatePickResult) => void
}) {
  const { t } = useTranslation()
  const LEVEL_LABELS = useLevelLabels()
  const reorder = useReorderTemplateAttributes()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = attrs.findIndex((a) => a.uuid === active.id)
      const newIndex = attrs.findIndex((a) => a.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      // Build new order
      const reordered = [...attrs]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)

      reorder.mutate({
        templateUuid,
        attribute_level: level,
        attributes: reordered.map((a, i) => ({ uuid: a.uuid, order: i })),
      })
    },
    [attrs, templateUuid, level, reorder],
  )

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          {LEVEL_LABELS[level]}
        </h3>
        <AttributeTemplatePicker
          onPick={onAddPick}
          resetAfterPick
          asAddButton
          domain="product"
          data-testid={`product-templates-add-attribute-picker-${level.toLowerCase()}`}
        />
      </div>
      {level === 'PRODUCT_VARIANT' && stockedNames.length > 0 && (
        <p className="mb-2 text-[12px] leading-[1.5] text-[var(--ink-3)]">
          {t('templates.variantStockNote', {
            defaultValue:
              'Each variant holds its own {{names}} list. Open a variant to load its values.',
            names: stockedNames.join(', '),
          })}
        </p>
      )}
      {attrs.length === 0 ? (
        <p className="py-2 text-[12px] text-[var(--ink-3)]">
          {t('templates.noAttributes')}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={attrs.map((a) => a.uuid)} strategy={verticalListSortingStrategy}>
            <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
              {attrs.map((attr, idx) => (
                <SortableAttributeRow
                  key={attr.uuid}
                  attr={attr}
                  isLast={idx === attrs.length - 1}
                  onEdit={() => onEditAttr(attr)}
                  templateUuid={templateUuid}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ─── Sortable Attribute Row ──────────────────────────────

function SortableAttributeRow(props: {
  attr: ProductAttribute
  isLast: boolean
  onEdit: () => void
  templateUuid: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.attr.uuid,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <AttributeRow {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  )
}

// ─── Attribute Row ───────────────────────────────────────

function AttributeRow({
  attr,
  isLast,
  onEdit,
  templateUuid,
  dragHandleProps,
}: {
  attr: ProductAttribute
  isLast: boolean
  onEdit: () => void
  templateUuid: string
  dragHandleProps?: Record<string, unknown>
}) {
  const { t } = useTranslation()
  const FIELD_TYPE_LABELS = useFieldTypeLabels()
  const deleteAttr = useDeleteTemplateAttribute()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const options = normaliseOptions(attr.configuration_data?.options)

  return (
    <div
      data-testid={`product-templates-attribute-${attr.uuid}`}
      className={cn(
        'flex flex-wrap items-center gap-2 sm:gap-3 px-4 py-3 group transition-all duration-100 hover:bg-[var(--bg-subtle)]',
        !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
      )}
    >
      {/* Drag handle */}
      {dragHandleProps && (
        <div
          className="shrink-0 touch-none cursor-grab text-[var(--ink-3)] active:cursor-grabbing"
          {...dragHandleProps}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-base text-[var(--ink)]">
            {attr.display_name}
          </span>
          {attr.description && (
            <span className="text-[11px] text-[var(--ink-3)]">
              - {attr.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-[var(--ink-3)]">
            {attr.code_name}
          </span>
          {isSelectType(attr.field_type) && options.length > 0 && (
            <span className="text-[10px] text-[var(--ink-3)]">
              · {t('templates.optionCount', { count: options.length })}
            </span>
          )}
        </div>
      </div>
      {/* Badges */}
      {readStockConfig(attr.configuration_data) && (
        <span className="text-[10px] text-[var(--green)]">
          {t('templates.loadedValuesBadge', 'Loaded values')}
        </span>
      )}
      <TypeBadge>{FIELD_TYPE_LABELS[attr.field_type]}</TypeBadge>
      {attr.required_to_save && (
        <span className="text-[10px] text-[var(--green)]">{t('templates.required')}</span>
      )}
      {!attr.required_to_save && attr.required_to_publish && (
        <span className="text-[10px] text-[var(--ink-3)]">{t('templates.publish')}</span>
      )}
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-30 group-hover:opacity-100 transition-opacity duration-150">
        <IconButton onClick={onEdit} title={t('templates.editAttribute')} data-testid={`product-templates-edit-${attr.uuid}`}>
          <Pencil className="h-3 w-3" />
        </IconButton>
        <IconButton onClick={() => setDeleteDialogOpen(true)} title={t('common.delete')} data-testid={`product-templates-delete-${attr.uuid}`}>
          <Trash2 className="h-3 w-3" />
        </IconButton>
      </div>
      <AttributeDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        attributeName={attr.display_name}
        description={
          <>
            Delete <strong>{attr.display_name}</strong>? This action cannot be undone.
          </>
        }
        successMessage={t('templates.toasts.attributeDeleted')}
        errorFallback={t('templates.toasts.attributeDeleteFailed')}
        isPending={deleteAttr.isPending}
        onDelete={({ force }) =>
          deleteAttr.mutateAsync({ templateUuid, attrUuid: attr.uuid, force })
        }
      />
    </div>
  );
}

// ─── Attribute Dialog (Create / Edit) ────────────────────

function AttributeDialog({
  templateUuid,
  open,
  onOpenChange,
  editing,
  prefillName = '',
  prefillLevel = 'PRODUCT',
  otherQrField = null,
}: {
  templateUuid: string
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: ProductAttribute | null
  prefillName?: string
  prefillLevel?: AttributeLevel
  otherQrField?: string | null
}) {
  const { t } = useTranslation()
  const FIELD_TYPE_LABELS = useFieldTypeLabels()
  const createAttr = useCreateTemplateAttribute()
  const updateAttr = useUpdateTemplateAttribute()
  const isEdit = !!editing

  const [currentFieldType, setCurrentFieldType] = useState<string>(editing?.field_type ?? 'TEXT')
  const [options, setOptions] = useState<string[]>(
    editing ? normaliseOptions(editing.configuration_data?.options) : [],
  )
  const [optionInput, setOptionInput] = useState('')
  const [validation, setValidation] = useState(() =>
    draftsFromRules(editing?.configuration_data?.validation_rules as ValidationRule[] | undefined),
  )
  // Read through a ref at submit rather than listing `validation` / `options`
  // in the useForm deps: a new object per keystroke (or each Enter on an
  // option chip) rebuilt the Form and remounted it, and display_name's
  // autoFocus stole the caret back to Display Name.
  const validationRef = useRef(validation)
  validationRef.current = validation
  const optionsRef = useRef(options)
  optionsRef.current = options

  function commitOption() {
    const trimmed = optionInput.trim()
    if (trimmed && !options.includes(trimmed)) {
      setOptions([...options, trimmed])
    }
    setOptionInput('')
  }

  function handleOptionKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      e.stopPropagation()
      commitOption()
    }
    if (e.key === 'Backspace' && !optionInput && options.length > 0) {
      setOptions(options.slice(0, -1))
    }
  }

  function removeOption(idx: number) {
    setOptions(options.filter((_, i) => i !== idx))
  }

  const fieldTypeOptions = FIELD_TYPES.map((ft) => ({
    label: FIELD_TYPE_LABELS[ft],
    value: ft,
  }))

  const attributeLevel = editing?.attribute_level ?? prefillLevel
  const existingStock = readStockConfig(editing?.configuration_data)
  const hasLoadedValues = Boolean(existingStock)
  const [fillMethod, setFillMethod] = useState<string>(
    hasLoadedValues ? 'stock' : 'typed',
  )

  const qrTaken = otherQrField
    ? t('templates.qr.printAsQrTaken', {
        defaultValue: 'Already on {{name}}. Turn it off there first.',
        name: otherQrField,
      })
    : ''
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'display_name',
        label: t('templates.displayName'),
        placeholder: t('templates.displayNamePlaceholder'),
        value: editing?.display_name ?? prefillName,
        autoFocus: true,
        required: 'Display name is required',
        onChange: (value, formContext) => {
          if (!isEdit) {
            formContext?.setFieldValue(
              'code_name',
              String(value ?? '')
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .replace(/\s+/g, '_')
                .replace(/_+$/, ''),
            )
          }
        },
      },
      {
        type: 'text',
        name: 'code_name',
        label: t('templates.codeName'),
        placeholder: t('templates.codeNamePlaceholder'),
        value:
          editing?.code_name ??
          prefillName
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .replace(/_+$/, ''),
        required: 'Code name is required',
      },
      {
        type: 'text',
        name: 'description',
        label: t('common.description'),
        placeholder: t('templates.descriptionPlaceholder'),
        value: editing?.description ?? '',
      },
      {
        type: 'select',
        name: 'field_type',
        label: t('templates.fieldType.label'),
        value: editing?.field_type ?? 'TEXT',
        options: fieldTypeOptions,
        onChange: (value) => setCurrentFieldType(String(value ?? 'TEXT')),
      },
      // One question, three answers. Picking one hides the other two: a
      // format rule and a stock list both constrain the same value, and an
      // admin offered both has no way to know which wins.
      ...(canUseLoadedValues(currentFieldType) ||
      isSelectType(currentFieldType as FieldType)
        ? ([
            {
              type: 'select',
              name: 'fill_method',
              label: t('templates.fillMethod.label', 'How is this filled in?'),
              value: hasLoadedValues
                ? 'stock'
                : isSelectType(currentFieldType as FieldType)
                  ? 'list'
                  : 'typed',
              onChange: (value) => setFillMethod(String(value ?? 'typed')),
              options: [
                {
                  label: t('templates.fillMethod.typed', 'Typed in'),
                  value: 'typed',
                },
                ...(isSelectType(currentFieldType as FieldType)
                  ? [
                      {
                        label: t(
                          'templates.fillMethod.list',
                          'Chosen from a fixed list',
                        ),
                        value: 'list',
                      },
                    ]
                  : []),
                ...(attributeLevel === 'HOLDING' &&
                canUseLoadedValues(currentFieldType)
                  ? [
                      {
                        label: t(
                          'templates.fillMethod.stock',
                          'Chosen from a list loaded on each product',
                        ),
                        value: 'stock',
                      },
                    ]
                  : []),
              ],
            },
          ] as FormField[])
        : []),
      ...(fillMethod === 'stock'
        ? ([
            {
              type: 'custom',
              name: 'stock_hint',
              label: '',
              render: () => (
                <p className="-mt-1 text-[12px] leading-[1.5] text-[var(--ink-3)]">
                  {t(
                    'templates.fillMethod.stockHint',
                    'Load the values on each product you sell. If a product has variants, each variant holds its own list.',
                  )}
                </p>
              ),
            },
            {
              type: 'checkbox',
              name: 'print_as_qr',
              label: t('templates.qr.printAsQr', 'Print as QR'),
              value: Boolean(readQrConfig(editing?.configuration_data)),
              hint: t('templates.qr.printAsQrHint'),
              onChange: (value, formContext) =>
                formContext?.setFieldError(
                  'print_as_qr',
                  value && qrTaken ? qrTaken : undefined,
                ),
            },
          ] as FormField[])
        : []),
      {
        type: 'checkbox',
        name: 'required_to_save',
        label: t('templates.requiredToSave'),
        value: editing?.required_to_save ?? false,
        hint: t('templates.requiredToSaveHint'),
      },
      {
        type: 'checkbox',
        name: 'required_to_publish',
        label: t('templates.requiredToPublish'),
        value: editing?.required_to_publish ?? true,
        hint: t('templates.requiredToPublishHint'),
      },
      {
        type: 'checkbox',
        name: 'visible_to_customer',
        label: 'Visible to customer',
        value: (editing?.visibility ?? 'PUBLIC') === 'PUBLIC',
        hint: 'Captured + visible to admins; uncheck to hide from the customer\'s record card.',
      },
    ],
    [
      editing,
      isEdit,
      t,
      fieldTypeOptions,
      prefillName,
      attributeLevel,
      currentFieldType,
      fillMethod,
      hasLoadedValues,
      qrTaken,
    ],
  )

  const formName = `product-attr-dialog-${editing?.uuid ?? 'new'}`
  const { Form, ref } = useForm(
    { name: formName, submit: 'footer', fields },
    async (rawValues, formContext) => {
      const values = rawValues as Record<string, unknown>
      const fieldType = values.field_type as string
      // Carry forward config this dialog does not edit (lookup_provider,
      // component_columns, consumer_only, ...); rebuilding from {} silently
      // dropped it. Only the two keys below are owned here.
      const configData: Record<string, unknown> = { ...(editing?.configuration_data ?? {}) }
      delete configData.options
      delete configData.validation_rules
      if (isSelectType(fieldType as FieldType) && optionsRef.current.length > 0) {
        configData.options = optionsToPayload(optionsRef.current)
      }
      const usesStock =
        attributeLevel === 'HOLDING' &&
        canUseLoadedValues(fieldType) &&
        values.fill_method === 'stock'

      // Stock is the allowed set, so a format rule on the same field could
      // only reject values the admin loaded themselves.
      if (!usesStock && (fieldType === 'TEXT' || fieldType === 'MULTI_SELECT')) {
        const rules = rulesFromDrafts(
          validationRef.current.drafts,
          validationRef.current.unmanaged,
        )
        if (rules.length > 0) configData.validation_rules = rules
      }
      if (usesStock) {
        delete configData.options
        configData.value_stock = {
          enabled: true,
          scope_entity_key: 'product',
          // Releasing on void is right for almost every tenant; retiring is not
          // worth a question before the admin has loaded a single value.
          on_void: 'release',
        }
        if (values.print_as_qr && qrTaken) {
          formContext?.setFieldError('print_as_qr', qrTaken)
          return
        }
        if (values.print_as_qr) {
          const existingQr = readQrConfig(editing?.configuration_data)
          configData.qr = {
            enabled: true,
            public_fields: existingQr?.public_fields ?? [],
          }
        } else {
          delete configData.qr
        }
      } else {
        delete configData.value_stock
        delete configData.qr
      }

      const payload = {
        display_name: (values.display_name as string).trim(),
        code_name: (values.code_name as string).trim(),
        description: (values.description as string).trim(),
        field_type: fieldType,
        // Level is fixed by the section the picker was opened from (or the
        // attribute being edited), not a form field.
        attribute_level: editing?.attribute_level ?? prefillLevel,
        required_to_save: Boolean(values.required_to_save),
        required_to_publish: Boolean(values.required_to_publish),
        visibility: values.visible_to_customer ? 'PUBLIC' : 'INTERNAL',
        configuration_data: configData,
      }

      try {
        if (isEdit && editing) {
          await updateAttr.mutateAsync({
            templateUuid,
            attrUuid: editing.uuid,
            ...payload,
          })
          toast.success(t('templates.toasts.attributeUpdated'))
        } else {
          await createAttr.mutateAsync({ templateUuid, ...payload })
          toast.success(t('templates.toasts.attributeAdded'))
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, isEdit ? t('templates.toasts.attributeUpdateFailed') : t('templates.toasts.attributeAddFailed'))
      }
    },
    [editing?.uuid ?? 'new', open, fields, prefillLevel],
  )

  // Hard-reset form + re-sync field-type/options local state on open: switching
  // which attribute you edit would otherwise show the previous one's config.
  useEffect(() => {
    if (!open) return
    const deriveCode = (name: string) =>
      name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+$/, '')
    ref.current?.resetForm({
      values: {
        display_name: editing?.display_name ?? prefillName,
        code_name: editing?.code_name ?? deriveCode(prefillName),
        description: editing?.description ?? '',
        field_type: editing?.field_type ?? 'TEXT',
        attribute_level: editing?.attribute_level ?? prefillLevel,
        required_to_save: editing?.required_to_save ?? false,
        required_to_publish: editing?.required_to_publish ?? true,
        visible_to_customer: (editing?.visibility ?? 'PUBLIC') === 'PUBLIC',
        fill_method: readStockConfig(editing?.configuration_data)
          ? 'stock'
          : isSelectType((editing?.field_type ?? 'TEXT') as FieldType)
            ? 'list'
            : 'typed',
        print_as_qr: Boolean(readQrConfig(editing?.configuration_data)),
      },
    })
    setCurrentFieldType(editing?.field_type ?? 'TEXT')
    setFillMethod(readStockConfig(editing?.configuration_data) ? 'stock' : 'typed')
    setOptions(editing ? normaliseOptions(editing.configuration_data?.options) : [])
    setOptionInput('')
    setValidation(
      draftsFromRules(editing?.configuration_data?.validation_rules as ValidationRule[] | undefined),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.uuid])

  const activeFieldType = currentFieldType as FieldType

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          const formRef = ref.current
          formRef?.resetForm()
          setCurrentFieldType(editing?.field_type ?? 'TEXT')
          setFillMethod(readStockConfig(editing?.configuration_data) ? 'stock' : 'typed')
          setOptions(editing ? normaliseOptions(editing.configuration_data?.options) : [])
          setOptionInput('')
          setValidation(
            draftsFromRules(
              editing?.configuration_data?.validation_rules as ValidationRule[] | undefined,
            ),
          )
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('templates.editAttribute') : t('templates.addAttribute')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <Form className="flex flex-col gap-4" />

          {isSelectType(activeFieldType) && fillMethod !== 'stock' && (
            <div>
              <Label className="clara-section-label mb-1.5 block">{t('templates.options')}</Label>
              <div
                className="min-h-[38px] cursor-text rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-3 py-2"
                onClick={() => document.getElementById('option-input')?.focus()}
              >
                {options.map((opt, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[11px] text-[var(--ink-2)]"
                  >
                    {opt}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); removeOption(idx) }}
                      className="h-auto w-auto cursor-pointer p-0 text-[var(--ink-3)]"
                    >
                      <X className="h-2.5 w-2.5" />
                    </Button>
                  </span>
                ))}
                <input
                  id="option-input"
                  type="text"
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={handleOptionKeyDown}
                  onBlur={commitOption}
                  placeholder={options.length === 0 ? t('templates.optionsPlaceholder') : ''}
                  className="min-w-[120px] flex-1 bg-transparent text-[13px] text-[var(--ink)] outline-none"
                />
              </div>
              <p className="mt-1 text-[10px] text-[var(--ink-3)]">{t('templates.optionsHint')}</p>
            </div>
          )}

          {fillMethod !== 'stock' && (
            <AttributeValidationEditor
              fieldType={activeFieldType}
              drafts={validation.drafts}
              onChange={(drafts) => setValidation((prev) => ({ ...prev, drafts }))}
            />
          )}
        </div>

        <DialogFooter>
          <GreenButton
            type="submit"
            form={formName}
            disabled={createAttr.isPending || updateAttr.isPending}
          >
            {isEdit ? t('common.save') : t('common.add')}
          </GreenButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Shared UI helpers ───────────────────────────────────

function GreenButton({
  children,
  onClick,
  disabled,
  type,
  form,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit'
  form?: string
  'data-testid'?: string
}) {
  return (
    <Button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 h-auto bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
    >
      {children}
    </Button>
  )
}

function TypeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
      {children}
    </span>
  );
}

function IconButton({
  children,
  onClick,
  title,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
  'data-testid'?: string
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      title={title}
      data-testid={dataTestId}
      className="h-6 w-6 cursor-pointer items-center justify-center rounded-[4px] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
    >
      {children}
    </Button>
  )
}
